import { formatearRevision, TAMANO_MAXIMO_ARCHIVO } from "@gestiondoc/shared";
import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { conAviso, useTRPC } from "../api.ts";
import { leerComoBase64, mimeDeArchivo } from "../utiles.ts";
import { Boton } from "./ui.tsx";

/**
 * Los botones de la cabecera del documento que mueven la revisión en curso sin modal: reemplazar
 * el archivo de un borrador y publicar un informativo.
 */

/** "Reemplazar archivo" (solo quien elabora, solo en borrador). */
export function ReemplazarArchivo({
  revisionId,
  onError,
}: {
  revisionId: string;
  onError: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const entrada = useRef<HTMLInputElement>(null);
  const reemplazar = useMutation(
    trpc.documentos.reemplazarArchivo.mutationOptions(conAviso(onError)),
  );

  async function elegido(f: File | undefined) {
    // Se vacía el campo: elegir el mismo archivo otra vez (tras un error) vuelve a disparar.
    if (entrada.current) entrada.current.value = "";
    if (!f || reemplazar.isPending) return;
    const mime = mimeDeArchivo(f);
    if (!mime) {
      onError("Solo se aceptan Word, Excel o PDF");
      return;
    }
    if (f.size > TAMANO_MAXIMO_ARCHIVO) {
      onError("El archivo supera los 25 MB");
      return;
    }
    const base64 = await leerComoBase64(f);
    reemplazar.mutate({ revisionId, archivo: { nombre: f.name, mime, base64 } });
  }

  return (
    <>
      <input
        ref={entrada}
        type="file"
        accept=".docx,.doc,.xlsx,.xls,.pdf"
        className="sr-only"
        onChange={(e) => elegido(e.target.files?.[0])}
      />
      <Boton
        variante="secundario"
        onClick={() => entrada.current?.click()}
        disabled={reemplazar.isPending}
      >
        {reemplazar.isPending ? "Subiendo…" : "Reemplazar archivo"}
      </Boton>
    </>
  );
}

/** Fase 3b: el botón "Publicar" de un documento informativo en borrador. */
export function Publicar({
  revisionId,
  numero,
  onError,
}: {
  revisionId: string;
  numero: number;
  onError: (m: string | null) => void;
}) {
  const trpc = useTRPC();
  const publicar = useMutation(trpc.documentos.publicar.mutationOptions(conAviso(onError)));
  return (
    <Boton
      variante="firma"
      disabled={publicar.isPending}
      onClick={() => publicar.mutate({ revisionId })}
    >
      {publicar.isPending ? "Publicando…" : `Publicar Rev. ${formatearRevision(numero)}`}
    </Boton>
  );
}
